import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { AlertShort, Loader } from '@sdir/sds';
import moment from 'moment';
import styled from 'styled-components';
import { useApi } from '@sdir/httpclient/lib/hooks/useApi';
// eslint-disable-next-line max-len
import { AssessmentsReportByPersonaIdentificationId } from '@sdir/httpclient/lib/clients/aps/assessor';
import { ErrorMessage } from '@sdir/utilities/lib/components';
import { isToggleOn } from '@sdir/blueprint.aps/lib/helpers/permissionUtilities';
import { DisplayDateFormat } from '../../../helpers';
import {
  AssessmentRow,
  AssessmentsHeader,
  AssessmentsHeaderMobile,
  TableDefinition,
} from '../../../helpers/TableHeaders';
import { SortableDataTable } from '../../Molecules';
import { assessmentApi } from '../../../httpclient';
import { ToggleAssessment } from '../../../constants/permissions';
import { useCurrentPerson, useFeatureToggles } from '../../../services/hooks';

export const MyAssessments: React.FC = () => {
  const intl = useIntl();
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const featureToggle = useFeatureToggles();
  const {
    person: { result: contactInfo },
  } = useCurrentPerson();

  const userPersonalNr = useMemo(() => {
    return contactInfo?.personalIdentificationNumber || '';
  }, [contactInfo]);

  const showAssessment = !isToggleOn(featureToggle?.result, ToggleAssessment, userPersonalNr);

  const {
    result: assessments,
    error,
    loading,
  } = useApi({
    apiFunction: () => {
      const query = {
        take: 100,
        skip: 0,
      };
      return assessmentApi.v1AssessmentAssessmentsbyidentificationidQueryPost(query);
    },
    callApiOnInit: true,
  });

  const tableMobile = useMemo<TableDefinition<AssessmentRow> | null>(() => {
    if (!assessments) return null;
    return {
      columns: AssessmentsHeaderMobile(intl.formatMessage),
      data: assessments.map((e) => ({
        completedDate: DisplayDateFormat(e.completionDate),
        completedDateValue: moment(e.completionDate),
        vessel: e.vesselName || '',
        vesselValue: e.vesselName || '',
        competence: e.assessmentType || '',
        assessor: e.assessorName || '',
        period: `${e.signOnDate ? DisplayDateFormat(e.signOnDate) : ''} - ${
          e.signOffDate ? DisplayDateFormat(e.signOffDate) : ''
        }`,
        periodStart: moment(e.signOnDate),
        periodEnd: moment(e.signOffDate),
        childContent: <RowMobileContent rowData={e} fm={intl.formatMessage} />,
      })),
      sortableKeys: ['competence'],
      collapsable: true,
      collapsableText: [
        intl.formatMessage({ id: 'general.table.viewdetails' }),
        intl.formatMessage({ id: 'general.table.hidedetails' }),
      ],
    };
  }, [assessments, intl]);

  const table = useMemo<TableDefinition<AssessmentRow> | null>(() => {
    if (!assessments) return null;

    return {
      columns: AssessmentsHeader(intl.formatMessage),
      data: assessments.map((e) => ({
        completedDate: DisplayDateFormat(e.completionDate),
        completedDateValue: moment(e.completionDate),
        vessel: e.vesselName || '',
        vesselValue: e.vesselName || '',
        competence: e.assessmentType || '',
        assessor: e.assessorName || '',
        period: `${e.signOnDate ? DisplayDateFormat(e.signOnDate) : ''} - ${
          e.signOffDate ? DisplayDateFormat(e.signOffDate) : ''
        }`,
        periodStart: moment(e.signOnDate),
        periodEnd: moment(e.signOffDate),
      })),
      sortableKeys: '*',
    };
  }, [assessments]);

  const showEmpty = !assessments || assessments.length === 0;

  useEffect(() => {
    function handleResize() {
      const isMobileScreen = !!(window?.innerWidth < 768);
      setIsMobile(isMobileScreen);
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  if (!showAssessment) {
    return <></>;
  }

  return (
    <Wrapper>
      <h2>{intl.formatMessage({ id: 'home.tile.assessment' })}</h2>
      {loading && <Loader />}
      {error && <ErrorMessage error={error} />}
      {showEmpty && !error && (
        <AlertShort text={intl.formatMessage({ id: 'assessment.notfound' })} type="info" />
      )}
      {table && tableMobile && !showEmpty && (
        <TableWrapper data-testid="assessment-table">
          <StyledSortableDataTable table={isMobile ? tableMobile : table} autoWidth />
        </TableWrapper>
      )}
    </Wrapper>
  );
};

interface RowMobileContentProps {
  rowData: AssessmentsReportByPersonaIdentificationId;
  fm: any;
}

const RowMobileContent: React.FC<RowMobileContentProps> = ({ rowData, fm }) => {
  return (
    <>
      <MobileContentItem>
        <SubHeader>{fm({ id: 'assessment.table.subheader1' })}</SubHeader>
        <SubValue>{DisplayDateFormat(rowData.completionDate)}</SubValue>
      </MobileContentItem>
      <MobileContentItem>
        <SubHeader>{fm({ id: 'assessment.table.subheader2' })}</SubHeader>
        <SubValue>{rowData.assessorName || ''}</SubValue>
      </MobileContentItem>
      <MobileContentItem>
        <SubHeader>{fm({ id: 'common.table.vessel' })}</SubHeader>
        <SubValue>{rowData.vesselName || ''}</SubValue>
      </MobileContentItem>
      <MobileContentItem>
        <SubHeader>{fm({ id: 'common.table.periode' })}</SubHeader>
        <SubValue>
          {`${rowData.signOnDate ? DisplayDateFormat(rowData.signOnDate) : ''} - ${
            rowData.signOffDate ? DisplayDateFormat(rowData.signOffDate) : ''
          }`}
        </SubValue>
      </MobileContentItem>
    </>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 40px;
`;

const TableWrapper = styled.div`
  max-width: 100%;
  overflow: auto;
`;

const StyledSortableDataTable = styled(SortableDataTable)`
  margin-left: -5px;
  padding: 0 0 20px 0;
  background-color: transparent;
  thead tr th {
    padding-left: 0;
    button {
      text-align: left;
    }
  }
  tbody tr {
    border-top: 1px solid ${({ theme }) => theme.colors.primary8};
    min-height: 40px;

    &:last-child {
      border-bottom: 1px solid ${({ theme }) => theme.colors.primary8};
    }
  }
`;

const MobileContentItem = styled.div`
  margin-bottom: 10px;
`;

const SubHeader = styled.span`
  display: block;
  font-weight: bold;
  margin-bottom: 2px;
`;

const SubValue = styled.span`
  display: block;
`;
